import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import classNames from 'classnames';
import { defaultTo, isArray, isEmpty, isFunction } from 'lodash-es';
import { CaretDown, CaretUp, Check } from 'phosphor-react';
import { useMemo, type JSX } from 'react';
import type { FormFieldVariant } from '../form-field-variant';
import { useOptionContainerWidthSync } from '../option-container-viewport-aware-resize';
import { useMenuOpenState } from './menu-open';
import type { EmptyOption, EmptyOptionValue, Option, OptionDecorator, OptionValue, OptionsOrProvider } from './option';
import { DefaultOptionDecorator, isEmptyOption, isOption, isOptionsProvider, toOptions } from './option';
import { OptionsFilter, useOptionsFilter } from './options-filter';
import { useValue } from './value';

type SelectProps = {
  className?: string;
  readOnly?: boolean;
  disabled?: boolean;
  variant?: FormFieldVariant;
  options: OptionsOrProvider;
  optionDecorator?: OptionDecorator;
  placeholder?: string | (() => JSX.Element);
  value: OptionValue | undefined | null;
} & ({
  addEmptyOption?: true;
  onValueChange: (value: (Option | EmptyOption)['value']) => void;
} | {
  addEmptyOption: false;
  onValueChange: (value: OptionValue) => void;
}) & ({
  displayOptionsFilter?: false;
  optionsFilterPlaceholder?: undefined;
} | {
  displayOptionsFilter?: true;
  optionsFilterPlaceholder?: string;
});

/**
 * @deprecated use Combobox or Listbox instead
 */
export function Select({
  addEmptyOption = true,
  className,
  readOnly = false,
  disabled = readOnly,
  displayOptionsFilter = false,
  optionsFilterPlaceholder = 'Durchsuchen',
  onValueChange,
  options: optionsOrValuesOrProvider,
  optionDecorator = DefaultOptionDecorator,
  placeholder: placeholderFn = 'Bitte wählen',
  value,
  variant = 'default',
}: SelectProps) {
  const { triggerRef, contentStyle } = useOptionContainerWidthSync<HTMLButtonElement>();
  const { closeMenu, menuOpen, rootProps } = useMenuOpenState(disabled);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { selectValue, selectedValue } = useValue(value, onValueChange as any, addEmptyOption);

  const handleValueChange = (value: EmptyOptionValue | OptionValue) => {
    selectValue(value);
    closeMenu();
  };

  const hasProvider = isOptionsProvider(optionsOrValuesOrProvider);

  const optionsOrProvider = useMemo(() => {
    if (hasProvider) {
      return optionsOrValuesOrProvider;
    }

    return toOptions(optionsOrValuesOrProvider);
  }, [hasProvider, optionsOrValuesOrProvider]);

  const { optionsFilterProps, contentRef, filteredOptions, filteredItemStyle, loading } = useOptionsFilter(optionsOrProvider, menuOpen, selectValue, closeMenu, addEmptyOption);
  const selectedOption = useMemo(() => {
    const options = isArray(optionsOrProvider) ? optionsOrProvider : filteredOptions;
    return options.find(option => option.value === selectedValue) ?? { label: selectedValue, value: selectedValue } as Option;
  }, [filteredOptions, optionsOrProvider, selectedValue]);

  if (!hasProvider && isEmpty(optionsOrProvider)) {
    return null;
  }

  const placeholder = isFunction(placeholderFn) ? placeholderFn() : placeholderFn;
  const label = isEmpty(selectedOption?.label) ? placeholder : selectedOption?.label;
  const isPlaceholder = label === placeholder;

  return (
    <DropdownMenu.Root {...rootProps}>
      <div className="tds-input-dropdown-wrapper">
        <DropdownMenu.Trigger
          disabled={disabled}
          ref={triggerRef}
          className={classNames('flex justify-between items-center tds-input-field', className, {
            'tds-input-field-default': variant === 'default',
            'tds-input-field-small': variant === 'small',
            'select-none': !readOnly,
            'bg-contrast text-dark': !disabled,
            'cursor-default': disabled && !readOnly,
            'bg-transparent px-0 cursor-default': readOnly,
          })}
        >
          <div className={classNames('flex-1 text-left tds-input-value whitespace-nowrap overflow-hidden', {
            'tds-input-value-default': variant === 'default',
            'tds-input-value-small': variant === 'small',
          })}>
            {isOption(selectedOption) ? optionDecorator(selectedOption) : null}
            {!isOption(selectedOption) ? <div className={classNames({ 'tds-input-value-placeholder': isPlaceholder })}>{label}</div> : null}
          </div>
          {menuOpen ? <CaretUp size={16} className="ml-200 tds-input-icon" /> : <CaretDown size={16} className={classNames('ml-200 tds-input-icon', { disabled })} />}
        </DropdownMenu.Trigger>

        <DropdownMenu.Content
          align="start"
          style={contentStyle}
          ref={contentRef}
          className={classNames('mt-2 select-none tds-option-container tds-input-value', {
            'tds-input-value-default': variant === 'default',
            'tds-input-value-small': variant === 'small',
          })}
        >
          {displayOptionsFilter ? <OptionsFilter {...optionsFilterProps} placeholder={optionsFilterPlaceholder} variant={variant} /> : null}
          <DropdownMenu.RadioGroup
            // TODO 1335 workaround for long lists in modals
            className="overflow-y-auto max-h-96"
            value={selectedValue}
            onValueChange={handleValueChange}
          >
            {!loading ? filteredOptions.map(option => (
              <Item option={option} optionDecorator={optionDecorator} className={classNames(
                'tds-option-container-item flex items-center justify-between aria-checked:pr-3',
                displayOptionsFilter ? filteredItemStyle : '',
              )} key={defaultTo(option.value, 'undefined')} />
            )) : null}
          </DropdownMenu.RadioGroup>
          {loading ? <LoadingIndicator /> : null}
        </DropdownMenu.Content>
      </div>
    </DropdownMenu.Root >
  );
}

type ItemProps = {
  option: Option | EmptyOption;
  optionDecorator: OptionDecorator;
  className?: string;
};

const Item = ({ option, optionDecorator, className }: ItemProps) => (
  <DropdownMenu.RadioItem
    className={className}
    value={option.value as string}
  >
    {isEmptyOption(option) ? (
      <div className="flex-1 text-medium">
        {isEmpty(option.label) ? 'Keine Auswahl' : option.label}
      </div>
    ) : null}

    {!isEmptyOption(option) ? (
      <span>
        {optionDecorator(option)}
      </span>
    ) : null}

    <DropdownMenu.ItemIndicator className="tds-input-icon">
      <Check size={14} />
    </DropdownMenu.ItemIndicator>
  </DropdownMenu.RadioItem>
);

const LoadingIndicator = () => <div className="tds-option-container-item">Lädt...</div>;
